import { Controller } from "@hotwired/stimulus"

// Progressive Sync Controller - Enhanced features for modern browsers
export default class extends Controller {
  static values = {
    applicationId: String,
    enableNotifications: { type: Boolean, default: true },
    enableVibration: { type: Boolean, default: true }
  }

  connect() {
    // console.log("Progressive sync controller connected")
    
    this.setupNotificationPermission()
    this.setupVisibilityChange()
    this.setupNetworkStatus()
    this.bindCustomEvents()
  }

  disconnect() {
    this.unbindCustomEvents()
  }

  // Request notification permission
  async setupNotificationPermission() {
    if (!this.enableNotificationsValue || !('Notification' in window)) {
      return
    }

    if (Notification.permission === 'default') {
      try {
        await Notification.requestPermission()
      } catch (error) {
        console.log("Notification permission denied")
      }
    }
  }

  // Handle page visibility changes
  setupVisibilityChange() {
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  handleVisibilityChange() {
    if (document.hidden) {
      // Page is hidden - reduce polling frequency
      this.dispatch('visibility-hidden')
    } else {
      // Page is visible - resume normal polling
      this.dispatch('visibility-visible')
      this.checkForUpdates()
    }
  }

  // Monitor network status
  setupNetworkStatus() {
    if ('navigator' in window && 'onLine' in navigator) {
      this.handleOnlineStatus = this.handleOnlineStatus.bind(this)
      this.handleOfflineStatus = this.handleOfflineStatus.bind(this)
      
      window.addEventListener('online', this.handleOnlineStatus)
      window.addEventListener('offline', this.handleOfflineStatus)
    }
  }

  handleOnlineStatus() {
    console.log("Connection restored - checking for sync updates")
    this.checkForUpdates()
    this.showNetworkStatus('Connected', 'success')
  }

  handleOfflineStatus() {
    console.log("Connection lost - will retry when online")
    this.showNetworkStatus('Offline - will retry when connected', 'warning')
  }

  // Bind custom events
  bindCustomEvents() {
    this.handleSalesforceUpdate = this.handleSalesforceUpdate.bind(this)
    document.addEventListener('salesforce:updated', this.handleSalesforceUpdate)
  }

  unbindCustomEvents() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    window.removeEventListener('online', this.handleOnlineStatus)
    window.removeEventListener('offline', this.handleOfflineStatus)
    document.removeEventListener('salesforce:updated', this.handleSalesforceUpdate)
  }

  // Handle Salesforce sync updates
  handleSalesforceUpdate(event) {
    const { applicationId, status, data } = event.detail
    
    if (applicationId === this.applicationIdValue) {
      this.handleStatusChange(status, data)
    }
  }

  // Handle status changes with enhanced UX
  handleStatusChange(status, data) {
    switch (status) {
      case 'synced':
        this.showSuccessNotification()
        this.triggerSuccessVibration()
        this.updateFavicon('success')
        break
      case 'failed':
        this.showErrorNotification(data.error)
        this.triggerErrorVibration()
        this.updateFavicon('error')
        break
      case 'pending':
        this.updateFavicon('pending')
        break
    }
  }

  // Show browser notification
  showSuccessNotification() {
    if (!this.enableNotificationsValue || Notification.permission !== 'granted') {
      return
    }

    const notification = new Notification('Application Synced Successfully', {
      body: 'Your loan application has been added to our CRM system.',
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: `sync-${this.applicationIdValue}`,
      requireInteraction: false
    })

    // Auto-close notification
    setTimeout(() => notification.close(), 5000)
  }

  showErrorNotification(error) {
    if (!this.enableNotificationsValue || Notification.permission !== 'granted') {
      return
    }

    const notification = new Notification('Sync Status Update', {
      body: 'There was a temporary issue with your application sync. Our team has been notified.',
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: `sync-${this.applicationIdValue}`,
      requireInteraction: false
    })

    setTimeout(() => notification.close(), 8000)
  }

  // Haptic feedback
  triggerSuccessVibration() {
    if (this.enableVibrationValue && 'vibrate' in navigator) {
      navigator.vibrate([100, 50, 100]) // Success pattern
    }
  }

  triggerErrorVibration() {
    if (this.enableVibrationValue && 'vibrate' in navigator) {
      navigator.vibrate([200, 100, 200, 100, 200]) // Error pattern
    }
  }

  // Update favicon to show status
  updateFavicon(status) {
    const favicon = document.querySelector('link[rel="icon"]')
    if (!favicon) return

    const originalHref = favicon.href
    
    // Store original if not already stored
    if (!this.originalFavicon) {
      this.originalFavicon = originalHref
    }

    // Create colored favicon based on status
    const canvas = document.createElement('canvas')
    canvas.width = 32
    canvas.height = 32
    const ctx = canvas.getContext('2d')

    // Base color
    const colors = {
      success: '#10B981', // green
      error: '#EF4444',   // red
      pending: '#F59E0B', // yellow
      default: '#3B82F6'  // blue
    }

    ctx.fillStyle = colors[status] || colors.default
    ctx.fillRect(0, 0, 32, 32)
    
    // Add text indicator
    ctx.fillStyle = 'white'
    ctx.font = 'bold 20px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    
    const indicators = {
      success: '✓',
      error: '!',
      pending: '⧖',
      default: '?'
    }
    
    ctx.fillText(indicators[status] || indicators.default, 16, 16)
    
    // Update favicon
    favicon.href = canvas.toDataURL()
    
    // Restore original favicon after delay (except for errors)
    if (status !== 'error') {
      setTimeout(() => {
        if (this.originalFavicon) {
          favicon.href = this.originalFavicon
        }
      }, 5000)
    }
  }

  // Show network status
  showNetworkStatus(message, type) {
    // Create/update network status indicator
    let indicator = document.getElementById('network-status-indicator')
    
    if (!indicator) {
      indicator = document.createElement('div')
      indicator.id = 'network-status-indicator'
      indicator.className = 'fixed bottom-4 right-4 z-50 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300'
      document.body.appendChild(indicator)
    }

    // Update styles based on type
    indicator.className = `fixed bottom-4 right-4 z-50 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
      type === 'success' ? 'bg-green-500 text-white' :
      type === 'warning' ? 'bg-yellow-500 text-white' :
      'bg-blue-500 text-white'
    }`
    
    indicator.textContent = message
    indicator.style.opacity = '1'

    // Auto-hide after delay
    setTimeout(() => {
      indicator.style.opacity = '0'
      setTimeout(() => {
        if (indicator.parentNode) {
          indicator.remove()
        }
      }, 300)
    }, 3000)
  }

  // Check for updates when page becomes visible
  async checkForUpdates() {
    try {
      const response = await fetch(`/admin/salesforce/status/${this.applicationIdValue}`, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (response.ok) {
        const data = await response.json()
        
        // Trigger update event
        document.dispatchEvent(new CustomEvent('salesforce:updated', {
          detail: {
            applicationId: this.applicationIdValue,
            status: data.status,
            data: data
          }
        }))
      }
    } catch (error) {
      console.log("Failed to check for sync updates:", error)
    }
  }
}